import { Link as RouterLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Button, 
  Typography, 
  Link, 
  Container, 
  Grid, 
  Paper, 
  Stack, 
  Divider 
} from '@mui/material';
import { supabase } from '../../components/supabaseClient';
import { useSettingsContext } from '../../components/settings';
import ThemeToggle from '../../components/settings/ThemeToggle';
import useSession from '../../hooks/useSession';

const Account = () => {
  const session = useSession();
  const { themeStretch } = useSettingsContext();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (session && session.user) {
      // getProfile();
    }
  }, [session]);

  const signOut = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error('Error during sign-out:', error.message || error);
    } catch (err) {
      console.error('Error during sign-out:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async () => {
    const confirm = window.confirm('Are you sure you want to delete your profile? This will remove all your data.');

    if (confirm) {
      try {
        setLoading(true);
        const { user } = session;

        const { error } = await supabase
          .from('users')
          .delete()
          .eq('id', user.id);

        if (error) {
          console.error('Error deleting user:', error);
        }

        await supabase.auth.signOut();
        setLoading(false);
        
        alert('Your profile has been deleted.');
      } catch (error) {
        alert(error.message);
      }
    }
  };

  if (!session || !session.user) {
    return (
      <>
        <Helmet>
          <title>Account Settings | Riva Data</title>
        </Helmet>
        <Container maxWidth={themeStretch ? false : 'sm'} sx={{ mt: 8 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              You must be logged in to view this page.
            </Typography>
            <Link component={RouterLink} to="/signin" color="primary" underline="none">
              <Button variant="outlined" sx={{ borderRadius: '50px', mt: 2 }}>
                Go to sign in
              </Button>
            </Link>
          </Paper>
        </Container>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Account Settings | Riva Data</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : 'md'} sx={{ mt: 8 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Account Settings
        </Typography>

        <Grid container spacing={4}>
          {/* Profile Section */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1, mt: 3 }}>
              Profile
            </Typography>
            <Paper
              sx={(theme) => ({
                p: 3,
                bgcolor:
                  theme.palette.mode === 'light'
                    ? theme.palette.background.neutral
                    : theme.palette.background.paper,
              })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                {loading ? (
                  <Typography variant="body1">Saving...</Typography>
                ) : (
                  <Typography variant="body1">
                    You are currently signed in with the email: {session.user.email}
                  </Typography>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={signOut}
                  sx={{ borderRadius: '50px' }}
                >
                  Sign out
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Theme Settings Section */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Theme Settings
            </Typography>
            <Paper
              sx={(theme) => ({
                p: 3,
                bgcolor:
                  theme.palette.mode === 'light'
                    ? theme.palette.background.neutral
                    : theme.palette.background.paper,
              })}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body1" fontWeight="medium">
                  Theme
                </Typography>
                <ThemeToggle />
              </Stack>
            </Paper>
          </Grid>

          {/* Subscription Section */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Subscription
            </Typography>
            <Paper
              sx={(theme) => ({
                p: 3,
                bgcolor:
                  theme.palette.mode === 'light'
                    ? theme.palette.background.neutral
                    : theme.palette.background.paper,
              })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">Your current plan: Free</Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Delete Profile Section */}
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Delete Profile
            </Typography>
            <Paper
              sx={(theme) => ({
                p: 3,
                bgcolor:
                  theme.palette.mode === 'light'
                    ? theme.palette.background.neutral
                    : theme.palette.background.paper,
              })}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Deleting your profile will remove all your data. This action cannot be undone.
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={deleteUser}
                  sx={{ borderRadius: '50px' }}
                >
                  Delete Profile
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Account;